var _excluded = ["onComplete", "length", "invalidErrorMessages", "requiredFieldsErrorMessages", "fieldAriaLabel"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) { try { var info = gen[key](arg); var value = info.value; } catch (error) { reject(error); return; } if (info.done) { resolve(value); } else { Promise.resolve(value).then(_next, _throw); } }

function _asyncToGenerator(fn) { return function () { var self = this, args = arguments; return new Promise(function (resolve, reject) { var gen = fn.apply(self, args); function _next(value) { asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value); } function _throw(err) { asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err); } _next(undefined); }); }; }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

import { createRef, useCallback, useEffect, useState } from 'react';
import { forwardRef } from '../../../utils';
import { useMultiStyleConfig } from '../../../utils/use-style-config';
import { Typography } from '../../dataDisplay';
import { _Form as Form, Box } from '../../internal';
import { TextField } from '../TextField';
import { checkIfAllValid, checkIsNumeric } from './verification-code-field.utils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
var DEFAULT_LENGTH = 4;
export var DEFAULT_INVALID_MESSAGE = 'The code does not match the latest code or the latest code is invalid';
export var DEFAULT_EMPTY_FIELD_INVALID_MESSAGE = 'All fields are required'; // TODO: Add local hooks: https://meliorisk.atlassian.net/browse/ME-23974

export var VerificationCodeField = forwardRef((_ref, ref) => {
  var {
    onComplete,
    length = DEFAULT_LENGTH,
    invalidErrorMessages,
    requiredFieldsErrorMessages,
    fieldAriaLabel = 'Verification code field'
  } = _ref,
      props = _objectWithoutProperties(_ref, _excluded);

  var styles = useMultiStyleConfig('VerificationCodeField', {
    length
  });
  var [refs, setRefs] = useState([]);
  var [isLoading, setIsLoading] = useState(false);
  var [values, setValues] = useState([]);
  var [isSubmitted, setIsSubmitted] = useState(false);
  var [isInvalid, setIsInvalid] = useState();
  var emptyFieldsInvalid = !values.every(value => value);
  var invalid = isInvalid || isSubmitted && emptyFieldsInvalid; // resets `isLoading` state and focuses the first input.

  useEffect(() => {
    if (isInvalid) {
      var _refs$;

      setIsLoading(false);
      var firstInput = (_refs$ = refs[0]) === null || _refs$ === void 0 ? void 0 : _refs$.current;
      firstInput && setInputFocus(firstInput);
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [isInvalid]); // prevent of showing `invalidErrorMessages` while the user fills the code.

  useEffect(() => {
    if (!emptyFieldsInvalid) {
      setIsInvalid(false);
    }
  }, [emptyFieldsInvalid]);
  useEffect(() => {
    // If new fields are added - add them with an empty string, and if they are lessned - remove the last fields.
    var updatedValues = length > values.length ? values.concat(Array(length - values.length).fill('')) : values.slice(0, length);
    setValues(updatedValues);
    setRefs(Array(length).fill('').map(() => /*#__PURE__*/createRef())); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [length]);
  var getInputByIndex = useCallback(siblingIndex => {
    var _refs$siblingIndex;

    return (_refs$siblingIndex = refs[siblingIndex]) === null || _refs$siblingIndex === void 0 ? void 0 : _refs$siblingIndex.current;
  }, [refs]);
  var setInputFocus = useCallback(input => {
    input.focus();
    if (input.value) setTimeout(() => {
      input.select();
    });
  }, []);
  var handleOnComplete = useCallback( /*#__PURE__*/function () {
    var _ref2 = _asyncToGenerator(function* (code) {
      // resets `isInvalid` & `isLoading`.
      setIsInvalid(false);
      setIsLoading(true);
      var isValid = yield onComplete(code); // sets `isInvalid` to `true` in case the `onComplete` returns `false`.

      if (!isValid) {
        setIsInvalid(!isValid);
      }
    });

    return function (_x) {
      return _ref2.apply(this, arguments);
    };
  }(), [onComplete]);
  var handleInput = useCallback( /*#__PURE__*/function () {
    var _ref3 = _asyncToGenerator(function* (index, isLast) {
      var _refs$index;

      if (!refs[index]) return;
      var input = (_refs$index = refs[index]) === null || _refs$index === void 0 ? void 0 : _refs$index.current;
      if (input !== null && input !== void 0 && input.value && !checkIsNumeric(input.value)) return;
      var nextInput = getInputByIndex(index + 1);
      var currentValues = values.map((v, i) => {
        var _input$value;

        return i === index ? (_input$value = input === null || input === void 0 ? void 0 : input.value) !== null && _input$value !== void 0 ? _input$value : '' : v;
      });
      var isComplete = isLast && checkIfAllValid(refs);
      setValues(currentValues);
      if (!(input !== null && input !== void 0 && input.value)) return;

      if (!isComplete && nextInput) {
        setInputFocus(nextInput);
      } else if (isComplete) {
        input.blur();
        yield handleOnComplete(currentValues.join(''));
      }
    });

    return function (_x2, _x3) {
      return _ref3.apply(this, arguments);
    };
  }(), // eslint-disable-next-line react-hooks/exhaustive-deps
  [onComplete, values]);
  var handleBackspace = useCallback(index => {
    var _refs$index2;

    if (!refs[index]) return;
    var input = (_refs$index2 = refs[index]) === null || _refs$index2 === void 0 ? void 0 : _refs$index2.current; // in case the input has value, set input state to empty string.

    if (input !== null && input !== void 0 && input.value) {
      var currentValues = values.map((v, i) => i === index ? '' : v);
      setValues(currentValues);
      return;
    }

    var prevInput = getInputByIndex(index - 1);
    if (prevInput) setInputFocus(prevInput);
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
  [values, getInputByIndex, setInputFocus]);
  var handleArrowKeyDown = useCallback(focusableIndex => {
    var focusableInput = getInputByIndex(focusableIndex);
    if (focusableInput) setInputFocus(focusableInput);
  }, [getInputByIndex, setInputFocus]);
  var handleKeyDown = useCallback((e, index) => {
    switch (e.code) {
      case 'Backspace':
        handleBackspace(index);
        break;

      case 'ArrowLeft':
        {
          var focusableIndex = index === 0 ? 0 : index - 1;
          handleArrowKeyDown(focusableIndex);
          break;
        }

      case 'ArrowRight':
        {
          var lastIndex = values.length - 1;

          var _focusableIndex = index === lastIndex ? lastIndex : index + 1;

          handleArrowKeyDown(_focusableIndex);
          break;
        }

      case 'KeyZ':
      case 'KeyY':
        {
          // prevent the user to use undo/redo to avoid bugs in react input behavior.
          e.preventDefault();
          return;
        }
    }
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
  [handleBackspace, handleArrowKeyDown]);
  var onSubmit = useCallback( /*#__PURE__*/function () {
    var _ref4 = _asyncToGenerator(function* (e) {
      e.preventDefault();
      setIsSubmitted(true);

      if (checkIfAllValid(refs)) {
        yield handleOnComplete(values.join(''));
      }
    });

    return function (_x4) {
      return _ref4.apply(this, arguments);
    };
  }(), // eslint-disable-next-line react-hooks/exhaustive-deps
  [handleOnComplete, values]);
  var handlePaste = useCallback( /*#__PURE__*/function () {
    var _ref5 = _asyncToGenerator(function* (e, index) {
      // paste should occur on the first input only.
      if (index > 0) return;
      e.preventDefault();
      var paste = e.clipboardData.getData('text');
      if (!checkIsNumeric(paste)) return;
      var updatedValues = values.map((value, i) => {
        var _paste$i;

        return (_paste$i = paste[i]) !== null && _paste$i !== void 0 ? _paste$i : value;
      });
      setValues(updatedValues);
      var updatedValuesAreValid = updatedValues.every(value => value);

      if (updatedValuesAreValid) {
        setIsLoading(true);
        yield handleOnComplete(updatedValues.join(''));
      } else {
        // after paste focuses the next input.
        var nextInput = getInputByIndex(paste.length);
        if (nextInput) setInputFocus(nextInput);
      }
    });

    return function (_x5, _x6) {
      return _ref5.apply(this, arguments);
    };
  }(), // eslint-disable-next-line react-hooks/exhaustive-deps
  [values]);
  var handleFocus = useCallback(index => {
    var _refs$index3;

    if (!refs[index]) return;
    var input = (_refs$index3 = refs[index]) === null || _refs$index3 === void 0 ? void 0 : _refs$index3.current;
    input && setInputFocus(input);
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
  [setInputFocus]);
  return /*#__PURE__*/_jsxs(Box, {
    __css: styles['container'],
    children: [/*#__PURE__*/_jsx(Form, _objectSpread(_objectSpread({
      isLoading: isLoading // eslint-disable-next-line @typescript-eslint/no-misused-promises
      ,
      onSubmit: onSubmit,
      ref: ref,
      "data-component": "VerificationCodeField",
      "data-testid": "verification-code-field"
    }, props), {}, {
      sx: styles['form'],
      children: values.map((value, i) => /*#__PURE__*/_jsx(TextField, {
        onFocus: () => handleFocus(i) // eslint-disable-next-line @typescript-eslint/no-misused-promises
        ,
        onInput: /*#__PURE__*/_asyncToGenerator(function* () {
          var isLast = i === values.length - 1;
          yield handleInput(i, isLast);
        }),
        onKeyDown: e => handleKeyDown(e, i) // eslint-disable-next-line @typescript-eslint/no-misused-promises
        ,
        onPaste: /*#__PURE__*/function () {
          var _ref7 = _asyncToGenerator(function* (e) {
            return yield handlePaste(e, i);
          });

          return function (_x7) {
            return _ref7.apply(this, arguments);
          };
        }(),
        "data-testid": "verification-code-field-input-".concat(i),
        ref: refs[i],
        isDisabled: isLoading,
        isInvalid: invalid,
        type: "text",
        maxLength: 1,
        value: value,
        size: "large",
        sx: styles['input'],
        "aria-label": "".concat(fieldAriaLabel, " ").concat(i + 1)
      }, "verification-code-field-input-".concat(i)))
    })), invalid && /*#__PURE__*/_jsx(Typography.Description, {
      isInvalid: invalid,
      label: emptyFieldsInvalid ? requiredFieldsErrorMessages !== null && requiredFieldsErrorMessages !== void 0 ? requiredFieldsErrorMessages : DEFAULT_EMPTY_FIELD_INVALID_MESSAGE : invalidErrorMessages !== null && invalidErrorMessages !== void 0 ? invalidErrorMessages : DEFAULT_INVALID_MESSAGE
    })]
  });
});
VerificationCodeField.displayName = 'VerificationCodeField';
//# sourceMappingURL=VerificationCodeField.js.map